import styled, { css } from "styled-components";
import { FC, useState } from "react";
import { AnimatePresence } from "framer-motion";

import Text from "@/components/Text";
import { ActionsWrap } from "@/components/Table";
import ActionsDropdown, {
  ActionsDropdownButton,
} from "@/components/ActionsDropdown";
import { ReactComponent as TrashIcon } from "@/assets/icons/trashbin.svg";
import { ReactComponent as PencilIcon } from "@/assets/icons/pencil.svg";
import ScriptCreationModal from "./ScriptCreationModal";
import {
  ProductionSceneResource,
  ProductionScriptResource,
} from "@/types/resources";
import useAlerts from "@/hooks/useAlerts";
import { api } from "@/utils/api";
import { useQueryClient } from "@tanstack/react-query";

const SceneNumber = styled(Text)`
  width: 16px;
  height: 16px;
  background: ${({ theme }) => theme.colors.gray};
  border-radius: 50%;
  display: flex;
  justify-content: center;
  align-items: center;
  margin: 0 6px;
  flex-shrink: 0;
  transition: background ease-out 0.2s;
`;

const ScriptTitleHolder = styled.div`
  display: flex;
  width: 100%;
`;

const StyledText = styled(Text)`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  transition: color ease-out 0.2s;
`;

const ScriptBox = styled.div<{ $active?: boolean }>`
  background: ${({ theme }) => theme.colors.white};
  border: 1px solid ${({ theme }) => theme.colors.grayscale};
  border-radius: 4px;
  flex: 1;
  padding-left: 24px;
  padding-right: 12px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  cursor: pointer;
  transition: border ease-out 0.2s;
  &:hover {
    border-color: rgba(34, 34, 34, 0.25);
  }
  ${({ theme, $active }) =>
    $active &&
    css`
      border-color: rgba(34, 34, 34, 0.25);
      ${SceneNumber} {
        background: ${theme.colors.purple};
      }
    `}
`;

const StyledActionsDropdown = styled(ActionsDropdown)`
  width: 170px;
`;

interface Props {
  script: ProductionScriptResource;
  active?: boolean;
  setActiveScriptId: (scriptId: number | null) => void;
}

const ScriptButton: FC<Props> = ({ active, script, setActiveScriptId }) => {
  const [showModal, setShowModal] = useState(false);

  const alerts = useAlerts();

  const queryClient = useQueryClient();

  const onDelete = async () => {
    try {
      const confirmed = await alerts.confirm(
        "Are you sure you would like to delete this script?",
        script.name
      );

      if (!confirmed) return;

      await api.delete(`/v1/production/scripts/${script.id}`);

      await queryClient.invalidateQueries(["production_projects"]);

      alerts.close();
    } catch (_) {
      alerts.error();
    }
  };

  const onDuplicate = async () => {
    try {
      const confirmed = await alerts.confirm(
        "Are you sure you would like to duplicate this script?",
        script.name
      );

      if (!confirmed) return;

      const { id } = await api.post<never, { id: number }>(
        `/v1/production/scripts/${script.id}/duplicate`
      );

      await queryClient.invalidateQueries(["production_projects"]);

      setActiveScriptId(id);

      alerts.success("New script has been successfully created");
    } catch (_) {
      alerts.error();
    }
  };

  const scriptScore = script.scenes?.reduce(
    (acc: number, curr: ProductionSceneResource) => {
      curr.highlights?.forEach((highlight) => {
        acc += highlight.value;
      });
      return acc;
    },
    0
  );

  return (
    <>
      <ScriptBox $active={active} onClick={() => setActiveScriptId(script.id)}>
        <ScriptTitleHolder>
          <StyledText $type="smallHeadline" $color={active ? "black" : "gray"}>
            {script.name}
          </StyledText>
          <SceneNumber $type="extraSmall" $color="white">
            {scriptScore}
          </SceneNumber>
        </ScriptTitleHolder>
        <ActionsWrap onClick={(e) => e.stopPropagation()}>
          <StyledActionsDropdown>
            <ActionsDropdownButton as="button" onClick={onDuplicate}>
              <span>
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 16 16"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M15 12V1H4"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M12 4H1V15H12V4Z"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </span>
              Duplicate
            </ActionsDropdownButton>
            <ActionsDropdownButton
              as="button"
              onClick={() => setShowModal(true)}
            >
              <span>
                <PencilIcon />
              </span>
              Edit name or link
            </ActionsDropdownButton>
            <ActionsDropdownButton as="button" onClick={onDelete}>
              <span>
                <TrashIcon />
              </span>
              Delete
            </ActionsDropdownButton>
          </StyledActionsDropdown>
        </ActionsWrap>
      </ScriptBox>
      <AnimatePresence>
        {showModal && (
          <ScriptCreationModal
            editableScript={script}
            onClose={() => setShowModal(false)}
          />
        )}
      </AnimatePresence>
    </>
  );
};

export default ScriptButton;
